import { formatNumber } from '@trips/shared/format';
import { useId } from 'react';
import s from './Charts.module.css';

/** Small hand-drawn SVG charts for the Stats tab. No chart library: a few paths each. */

const W = 320;
const H = 96;

/**
 * A smooth line through the points that never overshoots (monotone cubic, Fritsch-Carlson),
 * so a quiet year doesn't dip below zero between two busy ones.
 */
function smoothPath(pts: [number, number][]): string {
  const n = pts.length;
  if (n === 0) return '';
  if (n === 1) return `M${pts[0]![0]} ${pts[0]![1]}`;
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pts[i + 1]![0] - pts[i]![0]);
    slope.push((pts[i + 1]![1] - pts[i]![1]) / dx[i]!);
  }
  const t: number[] = [slope[0]!];
  for (let i = 1; i < n - 1; i++) {
    const a = slope[i - 1]!;
    const b = slope[i]!;
    t.push(
      a * b <= 0
        ? 0
        : (3 * (dx[i - 1]! + dx[i]!)) /
            ((2 * dx[i]! + dx[i - 1]!) / a + (dx[i]! + 2 * dx[i - 1]!) / b),
    );
  }
  t.push(slope[n - 2]!);
  let d = `M${pts[0]![0].toFixed(1)} ${pts[0]![1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const h = dx[i]! / 3;
    d += `C${(x0 + h).toFixed(1)} ${(y0 + t[i]! * h).toFixed(1)} ${(x1 - h).toFixed(1)} ${(y1 - t[i + 1]! * h).toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }
  return d;
}

/** Flights over time as a soft filled curve, with each value and label underneath. */
export function AreaChart({
  values,
  labels,
  label,
}: {
  values: number[];
  labels: string[];
  label: string;
}) {
  const id = useId();
  const max = Math.max(1, ...values);
  const n = values.length;
  // Points sit in the middle of each column so they line up with the labels below.
  const pts = values.map((v, i): [number, number] => [
    ((i + 0.5) / n) * W,
    H - 4 - (v / max) * (H - 12),
  ]);
  const line = smoothPath(pts);
  const first = pts[0];
  const last = pts[n - 1];
  const area =
    first && last ? `${line}L${last[0].toFixed(1)} ${H}L${first[0].toFixed(1)} ${H}Z` : '';
  const peak = values.indexOf(Math.max(...values));
  return (
    <figure className={s.area}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className={s.areaSvg}
        role="img"
        aria-label={`${label}: ${labels.map((l, i) => `${l} ${values[i]}`).join(', ')}`}
      >
        <defs>
          <linearGradient id={`${id}fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" className={s.fillTop} />
            <stop offset="1" className={s.fillBottom} />
          </linearGradient>
        </defs>
        <line x1="0" x2={W} y1={H - 0.5} y2={H - 0.5} className={s.base} />
        <path d={area} fill={`url(#${id}fill)`} />
        <path d={line} className={s.line} />
      </svg>
      <div className={s.cols} style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        {values.map((v, i) => (
          <span key={i} className={s.col}>
            <span className={`${s.colValue} num ${i === peak && v > 0 ? s.peak : ''}`}>
              {formatNumber(v)}
            </span>
            <span className={s.colLabel}>{labels[i]}</span>
          </span>
        ))}
      </div>
    </figure>
  );
}

export interface Slice {
  key: string;
  label: string;
  value: number;
  /** A CSS color, usually one of the shade variables in Charts.module.css. */
  color: string;
}

/** A ring split by share, with the biggest share in the middle. */
export function Donut({
  slices,
  center,
  caption,
}: {
  slices: Slice[];
  center: string;
  caption?: string;
}) {
  const total = slices.reduce((n, x) => n + x.value, 0);
  const r = 42;
  const c = 2 * Math.PI * r;
  const gap = slices.filter((x) => x.value > 0).length > 1 ? 2 : 0;
  let at = 0;
  return (
    <svg viewBox="0 0 100 100" className={s.donut} aria-hidden="true">
      <circle cx="50" cy="50" r={r} className={s.ring} />
      {total > 0 &&
        slices.map((x) => {
          if (!x.value) return null;
          const len = (x.value / total) * c;
          const el = (
            <circle
              key={x.key}
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke={x.color}
              strokeWidth="12"
              strokeDasharray={`${Math.max(0.5, len - gap)} ${c}`}
              strokeDashoffset={-at}
              transform="rotate(-90 50 50)"
            />
          );
          at += len;
          return el;
        })}
      <text
        x="50"
        y={caption ? 46 : 50}
        className={s.donutText}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {center}
      </text>
      {caption && (
        <text
          x="50"
          y="62"
          className={s.donutCaption}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {caption}
        </text>
      )}
    </svg>
  );
}

/** One bar split into shares, like window, middle and aisle. */
export function SplitBar({ slices }: { slices: Slice[] }) {
  const total = slices.reduce((n, x) => n + x.value, 0) || 1;
  return (
    <div className={s.split} aria-hidden="true">
      {slices.map((x) =>
        x.value ? (
          <span
            key={x.key}
            style={{ flexGrow: x.value / total, background: x.color }}
            className={s.splitPart}
          />
        ) : null,
      )}
    </div>
  );
}

/** Colored dots with labels and shares, for the donut and split bar. */
export function Legend({ slices }: { slices: Slice[] }) {
  const total = slices.reduce((n, x) => n + x.value, 0) || 1;
  return (
    <ul className={s.legend}>
      {slices.map((x) => (
        <li key={x.key} className={s.legendRow}>
          <span className={s.swatch} style={{ background: x.color }} />
          <span className={s.legendLabel}>{x.label}</span>
          <span className="num muted">{Math.round((x.value / total) * 100)}%</span>
          <span className={`${s.legendCount} num`}>{formatNumber(x.value)}</span>
        </li>
      ))}
    </ul>
  );
}

/** A thin bar for ranked lists, as a share of the top value. */
export function Meter({ share, tone }: { share: number; tone?: string }) {
  return (
    <span className={s.meter} aria-hidden="true">
      <span
        className={s.meterFill}
        style={{ width: `${Math.max(2, share * 100)}%`, background: tone }}
      />
    </span>
  );
}
